import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { Button, Form, Header, Icon, Message, Segment, Table, TextArea } from 'semantic-ui-react';
import {
  createMcpToken,
  listMcpTokens,
  McpTokenResponse,
  McpTokenSummary,
  revokeAllMcpTokens,
  revokeMcpToken,
  RevokeScope,
} from '~/api/admin';

const EXPIRY_OPTIONS = [
  { key: 7, value: 7, text: '7 days' },
  { key: 30, value: 30, text: '30 days' },
  { key: 90, value: 90, text: '90 days' },
];

const MCP_SERVER_NAME = 'conductor-ops';
const TOKENS_QUERY_KEY = 'admin-mcp-tokens';

function describeToken(token: McpTokenSummary): { what: string; detail: string } {
  if (token.kind === 'refresh') {
    return { what: 'Claude connector', detail: token.clientName ?? 'MCP client' };
  }
  if (token.source === 'oauth') {
    return { what: 'Connector session', detail: token.clientName ?? token.label ?? '' };
  }
  return { what: 'Claude Code token', detail: token.label ?? 'no label' };
}

function formatDate(value: string | null): string {
  return value ? new Date(value).toLocaleString() : '';
}

const MCP_URL = `${process.env.REACT_APP_API_ROOT ?? ''}/mcp`;

function claudeCodeCommand(minted: McpTokenResponse): string {
  return `claude mcp add --transport http ${MCP_SERVER_NAME} ${minted.mcpUrl} --header "Authorization: Bearer ${minted.token}"`;
}

async function copyToClipboard(value: string, what: string) {
  try {
    await navigator.clipboard.writeText(value);
    toast.success(`Copied ${what} to clipboard.`);
  } catch (err) {
    console.error(err);
    toast.error(`Could not copy ${what}. Select the text and copy it manually.`);
  }
}

export default function McpAccessPanel() {
  const [label, setLabel] = useState('');
  const [expiresInDays, setExpiresInDays] = useState<number>(30);
  const [minted, setMinted] = useState<McpTokenResponse | null>(null);
  const queryClient = useQueryClient();
  const tokensQuery = useQuery(TOKENS_QUERY_KEY, listMcpTokens);
  const tokens = tokensQuery.data ?? [];

  useEffect(() => {
    document.title = 'Claude MCP Access | Conductor Solar';
  }, []);

  const createMutation = useMutation({
    mutationFn: () => createMcpToken({ label: label.trim() || undefined, expiresInDays }),
    onSuccess(result) {
      setMinted(result);
      queryClient.invalidateQueries(TOKENS_QUERY_KEY);
      toast.success('MCP token created. Copy it now; it will not be shown again.');
    },
    onError(err: any) {
      const message = err?.response?.data?.message;
      toast.error(message ? `Could not create token: ${message}` : 'Could not create token. Please try again.');
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (tokenId: string) => revokeMcpToken(tokenId),
    onSuccess(_result, tokenId) {
      toast.success('Token revoked.');
      if (minted?.tokenId === tokenId) {
        setMinted(null);
      }
      queryClient.invalidateQueries(TOKENS_QUERY_KEY);
    },
    onError(err: any) {
      const message = err?.response?.data?.message;
      toast.error(message ? `Could not revoke token: ${message}` : 'Could not revoke token. Please try again.');
    },
  });

  const revokeAllMutation = useMutation({
    mutationFn: (scope: RevokeScope) => revokeAllMcpTokens(scope),
    onSuccess(result, scope) {
      const count = `${result.revoked} token${result.revoked === 1 ? '' : 's'}`;
      toast.success(
        scope === 'everyone'
          ? `Revoked ${count} across ${result.users ?? 0} admin account${result.users === 1 ? '' : 's'}.`
          : `Revoked ${count}.`,
      );
      setMinted(null);
      queryClient.invalidateQueries(TOKENS_QUERY_KEY);
    },
    onError(err: any) {
      const message = err?.response?.data?.message;
      toast.error(message ? `Could not revoke tokens: ${message}` : 'Could not revoke tokens. Please try again.');
    },
  });

  const confirmRevokeAll = () => {
    if (window.confirm('Revoke every MCP token and Claude connector for your account? Sessions using them stop working immediately.')) {
      revokeAllMutation.mutate('mine');
    }
  };

  const confirmRevokeEveryone = () => {
    if (
      window.confirm(
        'Revoke MCP access for EVERY admin? All tokens, Claude connectors and pending authorizations across all admin accounts stop working immediately. Each admin will need to reconnect. This action is logged with your account.',
      )
    ) {
      revokeAllMutation.mutate('everyone');
    }
  };

  return (
    <div>
      <Message info>
        <Message.Header>Read-only access for Claude sessions</Message.Header>
        <p>
          Claude (claude.ai, Claude Desktop or Claude Code) can read data from this environment on your behalf:
          users, companies, projects, portfolios, activity, notifications and read-only SQL. Nothing can be
          changed through it. Access is tied to your admin account and stops working if your account is
          deactivated or loses the admin role.
        </p>
      </Message>

      <Segment>
        <Header as="h3">Connect from claude.ai or Claude Desktop</Header>
        <p>
          No token is needed. In Claude, go to <strong>Settings → Connectors → Add custom connector</strong> and
          enter this URL:
        </p>
        <Form>
          <Form.Field>
            <TextArea readOnly rows={1} value={MCP_URL} style={{ fontFamily: 'monospace' }} />
          </Form.Field>
          <Button type="button" onClick={() => copyToClipboard(MCP_URL, 'MCP URL')}>
            <Icon name="copy" /> Copy URL
          </Button>
        </Form>
        <p style={{ marginTop: '1rem' }}>
          Claude will open a Conductor sign-in and consent screen. Approve it once with your admin account; Claude
          then keeps its own access and refreshes it automatically. Remove the connector in Claude to disconnect.
        </p>
      </Segment>

      <Segment>
        <Header as="h3">Create a token for Claude Code</Header>
        <p>Personal tokens are for command-line use, where a browser sign-in is not available.</p>
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            createMutation.mutate();
          }}
        >
          <Form.Group widths="equal">
            <Form.Input
              label="Label (optional)"
              placeholder="e.g. work laptop"
              value={label}
              maxLength={100}
              onChange={(_e, data) => setLabel(data.value)}
            />
            <Form.Select
              label="Expires in"
              options={EXPIRY_OPTIONS}
              value={expiresInDays}
              onChange={(_e, data) => setExpiresInDays(Number(data.value))}
            />
          </Form.Group>
          <Button type="submit" primary loading={createMutation.isLoading} disabled={createMutation.isLoading}>
            Generate token
          </Button>
        </Form>
      </Segment>

      {minted && (
        <Segment>
          <Header as="h3">Your new token</Header>
          <Message warning>
            <Icon name="warning sign" />
            This token is shown once. Store it somewhere safe. Token id <code>{minted.tokenId}</code>
            {minted.label ? ` (${minted.label})` : ''} expires {new Date(minted.expiresAt).toLocaleString()}.
          </Message>

          <Form>
            <Form.Field>
              <label>Token</label>
              <TextArea readOnly rows={4} value={minted.token} style={{ fontFamily: 'monospace' }} />
            </Form.Field>
            <Button type="button" onClick={() => copyToClipboard(minted.token, 'token')}>
              <Icon name="copy" /> Copy token
            </Button>

            <Form.Field style={{ marginTop: '1.5rem' }}>
              <label>Add to Claude Code</label>
              <TextArea readOnly rows={3} value={claudeCodeCommand(minted)} style={{ fontFamily: 'monospace' }} />
            </Form.Field>
            <Button type="button" onClick={() => copyToClipboard(claudeCodeCommand(minted), 'command')}>
              <Icon name="copy" /> Copy command
            </Button>
            <p style={{ marginTop: '1rem' }}>
              MCP endpoint: <code>{minted.mcpUrl}</code>. For Claude Desktop or claude.ai, add a custom connector
              with this URL and send the token as a bearer <code>Authorization</code> header.
            </p>
          </Form>
        </Segment>
      )}

      <Segment>
        <Header as="h3">Active access</Header>
        <p>
          Every live token for your account: Claude Code tokens you generated and Claude connectors you approved.
          Revoking one cuts that session off on its next request.
        </p>
        {tokensQuery.isError && <Message negative>Could not load your tokens. Reload the page to try again.</Message>}
        {tokensQuery.isSuccess && tokens.length === 0 && <p>You have no active MCP tokens or connectors.</p>}
        {tokens.length > 0 && (
          <Table compact>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Type</Table.HeaderCell>
                <Table.HeaderCell>Label / client</Table.HeaderCell>
                <Table.HeaderCell>Id</Table.HeaderCell>
                <Table.HeaderCell>Created</Table.HeaderCell>
                <Table.HeaderCell>Expires</Table.HeaderCell>
                <Table.HeaderCell />
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {tokens.map((token) => {
                const { what, detail } = describeToken(token);
                return (
                  <Table.Row key={token.id}>
                    <Table.Cell>{what}</Table.Cell>
                    <Table.Cell>{detail}</Table.Cell>
                    <Table.Cell>
                      <code title={token.id}>{token.id.slice(0, 12)}…</code>
                    </Table.Cell>
                    <Table.Cell>{formatDate(token.createdAt)}</Table.Cell>
                    <Table.Cell>{formatDate(token.expiresAt)}</Table.Cell>
                    <Table.Cell textAlign="right">
                      <Button
                        size="small"
                        negative
                        basic
                        loading={revokeMutation.isLoading && revokeMutation.variables === token.id}
                        disabled={revokeMutation.isLoading || revokeAllMutation.isLoading}
                        onClick={() => revokeMutation.mutate(token.id)}
                      >
                        Revoke
                      </Button>
                    </Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table>
        )}
        <Button
          negative
          loading={revokeAllMutation.isLoading}
          disabled={tokens.length === 0 || revokeAllMutation.isLoading || revokeMutation.isLoading}
          onClick={confirmRevokeAll}
        >
          <Icon name="ban" /> Revoke all
        </Button>
      </Segment>

      <Segment color="red">
        <Header as="h3">Emergency: revoke access for every admin</Header>
        <p>
          Cuts off every MCP token, Claude connector and pending authorization for <strong>all</strong> admin
          accounts, not just yours. Use this if a token may have leaked or a Claude session is misbehaving and you
          cannot tell whose it is. Every admin will need to reconnect. Any admin can do this; the action is logged
          with your account.
        </p>
        <Button
          negative
          loading={revokeAllMutation.isLoading && revokeAllMutation.variables === 'everyone'}
          disabled={revokeAllMutation.isLoading || revokeMutation.isLoading}
          onClick={confirmRevokeEveryone}
        >
          <Icon name="warning sign" /> Revoke all MCP access for every admin
        </Button>
      </Segment>
    </div>
  );
}
